<template>
  <div>
    <NavigationBreadcrumb :items="breadcrumb_items"></NavigationBreadcrumb>
    <h3 class="mt-3">
      {{ $getVisibleNames("mesh.campus", true, "Sedes") }}
      <AlternativeNameButton
        :table_name="'mesh.campus'"
        :allows_crud="allows_crud"
      ></AlternativeNameButton>
    </h3>
    <div style="display: flex">
      <div class="col" style="text-align: left">
        <ButtonMedium
          v-can="'mesh.add_campus'"
          v-if="allows_crud"
          :tooltip_text="
            $t('reusable.common.addTooltip', {
              entity: $getVisibleNames('mesh.campus', false, 'Sede'),
            })
          "
          :text_button="$t('reusable.common.add')"
          v-b-modal.new-campus-modal
          :icon="'plus'"
          :variant="'primary'"
        >
        </ButtonMedium>
      </div>
    </div>
    <GenericBTable
      v-if="campusList"
      :items="campusList"
      :fields="fields_campuses"
      :pagination="campusList.length"
      :filterCustom="filterCustom"
      @emitChangeSearchField="changeInputSearch"
      primary-key="id"
      :filter="input_search"
      :show_pagination="true"
      :search_filter="true"
      :columns_display="true"
      :allows_crud="allows_crud"
      :display_id="'campus'"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
    >
      <template v-slot:cell(school)="row">
        {{ getInstitutionName(row.item.school) }}
      </template>
      <template v-slot:cell(address_line)="row">
        {{ getAddressName(row.item.address) }}
      </template>
      <template v-if="allows_crud" v-slot:cell(actions)="row">
        <b-modal
          :id="`edit-campus-modal-${row.item.id}`"
          :title="
            $t('reusable.common.editModalTitle', {
              entity: $getVisibleNames('mesh.campus', false, 'Sede'),
            })
          "
          size="lg"
          hide-footer
        >
          <CampusForm
            :Campus="row.item"
            @updated="slotUpdatedCampus"
            @created_address="slotCreatedAddress"
            @updated_address="slotUpdatedAddress"
          ></CampusForm>
        </b-modal>
        <ButtonSmall
          v-can="'mesh.change_campus'"
          :tooltip_text="
            $t('reusable.common.editTooltip', {
              entity: $getVisibleNames('mesh.campus', false, 'Sede'),
            })
          "
          :click_button="
            () => $bvModal.show(`edit-campus-modal-${row.item.id}`)
          "
          :class="``"
          :icon="'square'"
        ></ButtonSmall>
        <ButtonSmall
          v-can="'mesh.delete_campus'"
          :click_button="() => askForDeleteCampus(row.item.id)"
          :tooltip_text="
            $t('reusable.common.deleteTooltip', {
              entity: $getVisibleNames('mesh.campus', false, 'Sede'),
            })
          "
          :icon="'trash'"
        ></ButtonSmall>
      </template>
    </GenericBTable>
    <div>
      <b-modal
        id="new-campus-modal"
        :title="
          $t('reusable.common.createModalTitle', {
            entity: $getVisibleNames('mesh.campus', false, 'Sede'),
          })
        "
        size="lg"
        hide-footer
      >
        <CampusForm
          @created_address="slotCreatedAddress"
          @created="slotCreatedCampus"
        ></CampusForm>
      </b-modal>
    </div>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";

export default {
  name: "CampusListView",
  props: {
    display_id: {},
  },
  components: {
    CampusForm: () => import("./CampusForm"),
    GenericBTable: () => import("@/components/reusable/GenericBTable"),
    AlternativeNameButton: () =>
      import("@/components/reusable/AlternativeNameButton"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
    NavigationBreadcrumb: () =>
      import("@/components/reusable/NavigationBreadcrumb"),
  },
  data() {
    return {
      input_search: "",
      sortBy: "",
      sortDesc: false,
      address: [],
    };
  },
  computed: {
    ...mapGetters({
      communes: names.COMMUNES,
      campuses: names.CAMPUSES,
      school: "getInstitution",
    }),
    breadcrumb_items() {
      return [
        {
          text: this.$t("reusable.breadcrumb.home"),
          to: "/home",
        },
        {
          text: this.$getVisibleNames("mesh.campus", true, "Sedes"),
        },
      ];
    },
    allows_crud() {
      const has_permission = this.$hasPermissions("mesh.add_campus");
      if (has_permission) return has_permission;
      else return false;
    },
    campusList() {
      if (this.school)
        return this.campuses.filter((x) => x.school == this.school.id);
      else
        console.log(
          this.$t("organizationalDefinitions.campus.institutionNotFound")
        );
      return this.campuses;
    },
    fields_campuses() {
      return [
        {
          key: "name",
          label: String(this.$getVisibleNames("mesh.campus", true, "Sedes")),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "address",
          label: this.$getVisibleNames("common.commune", false, "Comuna"),
          sortable: true,
          // La celda muestra el nombre de la comuna, no el id de la dirección
          sortByFormatted: true,
          display_column: true,
          tdClass: "text-left",
          formatter: (value) => this.getCommuneName(value),
        },
        {
          key: "address_line",
          label: this.$t("reusable.common.address"),
          sortable: false,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "phone",
          label: this.$t("organizationalDefinitions.campus.phone"),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        {
          key: "actions",
          label: "",
          display_column: true,
          thClass: "noprint",
          tdClass: "noprint",
        },
      ];
    },
  },
  methods: {
    filterCustom(row) {
      if (
        this.$filtered(row.name, this.input_search) ||
        this.$filtered(row.phone, this.input_search) ||
        this.$filtered(this.getAddressName(row.address), this.input_search) ||
        this.$filtered(this.getCommuneName(row.address), this.input_search)
      ) {
        return true;
      } else return false;
    },
    changeInputSearch(input_search) {
      this.input_search = input_search;
    },
    getInstitutionName(institution_id) {
      if (this.school && institution_id == this.school.id)
        return this.school.name;
      return "-";
    },
    getCommuneName(address_id) {
      if (!address_id) return "";
      else {
        const address = this.address.find((x) => x.id == address_id);
        if (address) {
          const commune = this.communes.find((x) => x.id == address.commune);
          return commune ? commune.name : "";
        } else return "";
      }
    },
    getAddressName(address_id) {
      const address = this.address.find((x) => x.id == address_id);
      return address ? address.line_1 : "";
    },
    slotCreatedAddress(response) {
      this.address.push(response);
    },
    slotUpdatedAddress(response) {
      const index = this.address.findIndex((x) => x.id == response.id);
      if (index != -1) {
        this.address[index].commune = response.commune;
        this.address[index].zip_code = response.zip_code;
        this.address[index].line_1 = response.line_1;
        this.address[index].line_2 = response.line_2;
      }
    },
    slotCreatedCampus() {
      this.$bvModal.hide("new-campus-modal");
    },
    slotUpdatedCampus(campus) {
      this.$bvModal.hide(`edit-campus-modal-${campus.id}`);
    },
    askForDeleteCampus(campus_id) {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle", {
          entity: this.$getVisibleNames("mesh.campus", false, "Sede"),
        }),
        text: this.$t("reusable.common.deleteConfirmText"),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.$store.dispatch(names.DELETE_CAMPUS, campus_id).then(() => {
            toast(
              String(
                this.$getVisibleNames("mesh.campus", false, "Sede") +
                  " " +
                  this.$t("reusable.common.deleted")
              )
            );
          });
        }
      });
    },
  },
  mounted() {},
  created() {
    this.sortBy = "name";
    this.$store.dispatch(names.FETCH_CAMPUSES);
    this.$store.dispatch(names.FETCH_REGIONS);
    this.$store.dispatch(names.FETCH_COUNTRIES);
    this.$store.dispatch(names.FETCH_COMMUNES);
    this.$restful.GetAll(`/common/address/`).then((response) => {
      this.address = response;
    });
  },
};
</script>

<style scoped>
.custom-td {
  width: 45% !important;
}
.campus-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}
.add-campus {
  margin-left: 70% !important;
  margin-bottom: -8%;
  padding-top: 1.3%;
}
.custon-tooltip {
  margin-top: 10px;
  z-index: 1;
}
.custon-tooltip::after {
  margin-right: 0.2em;
  transition: 0.2s ease-out;
}
</style>